/**
 * Spec 004.5 PR1p — TS-side SpinOutcome[] → MetricsResult fold.
 *
 * **Minimal aggregator.** The contract
 * (`engine-adapter-interface.md`) specifies "the WASM module
 * returns a JSON string. WasmWebGpuEngineAdapter calls JSON.parse()
 * once and casts to MetricsResult. No field projection." The
 * current wasm pkg returns `SpinOutcome[]` JSON (see
 * `crates/slot-engine-wgpu/src/wasm_bindings.rs`); a full Rust-
 * side aggregation that emits `MetricsResult` directly is a
 * substantial follow-up (must mirror the spec 006.1 native
 * aggregator). Until that lands, the JS adapter fills the gap.
 *
 * Coverage of MetricsResult fields:
 * - **Populated**: `rtp.point`, `rtp.ci_*`, `volatility.stdev` +
 *   `class_`, `hit_frequency`, `histogram.buckets`,
 *   `max_win_observed`, `feature_triggers` (counts only),
 *   `n_spins`, `schema_version`, `config_hash` (placeholder).
 * - **Best-effort (spec 013)**: `per_feature_award_histogram` — folded
 *   from `state_path` award steps when present; `{}` otherwise (the
 *   wgpu/WASM path emits a synthetic state_path without award detail).
 * - **Empty/zero placeholders**: `max_win_bound`,
 *   `bonus_contribution`, `per_symbol_contribution`,
 *   `rtp_per_rule`, `hits_per_rule`, `observed_bounds`,
 *   `insufficient_sample_flags`, `volatility.stdev_noncoinciding`.
 *
 * The placeholder fields render as zeros / empty arrays in the
 * panel components. PR1p ships the headline + histogram + bonus
 * panels working with real numbers; the per-rule / per-symbol
 * panels show "data not available in current build" until the
 * Rust-side aggregator backfills them.
 */

import type {
  Histogram,
  HistogramBucket,
  MetricsResult,
  RtpStat,
  VolatilityStat,
} from "./types";

// ──────────────────────────────────────────────────────────────────
// Internal SpinOutcome shape — matches the wasm pkg's return
// (slot-engine-core::types::SpinOutcome serialised via serde).
// ──────────────────────────────────────────────────────────────────

interface SpinOutcome {
  winnings: number;
  grid: string[][];
  state_path: unknown[];
  features_fired: string[];
  rng_draws_consumed: number;
}

/**
 * 15-bucket log-scaled histogram boundaries. Matches the
 * `slot-engine-core::aggregate::histogram::HISTOGRAM_BUCKETS_LOG_SCALE`
 * canonical scheme: bucket 0 captures winnings == 0; the remaining
 * 14 cover positive winnings on a log scale up to `1e6` (configurable
 * via the slot spec, but the default cap suffices for Tomb Explorer
 * and the 004.5 v1 corpus).
 */
const HISTOGRAM_LOG_BOUNDARIES: readonly number[] = [
  // [0, 0] — zero-win bucket
  0,
  // log-spaced positive buckets
  0.01, 0.1, 0.5, 1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 1e6,
];

// ──────────────────────────────────────────────────────────────────
// Public entry
// ──────────────────────────────────────────────────────────────────

/**
 * Fold an array of `SpinOutcome` (parsed from the wasm pkg's
 * Uint8Array JSON envelope) into a `MetricsResult`. See module-
 * level comment for the field-coverage caveat.
 *
 * @param outcomes  Parsed SpinOutcome array from the wasm bundle.
 * @param nSpins    Echo of the original request count; used in
 *                  the result envelope without recounting.
 * @param configHash Placeholder for the spec-001 config hash; the
 *                  wasm pkg doesn't currently emit it. Set to the
 *                  empty string when unknown — callers can patch
 *                  later if they hash the source-side config.
 */
export function aggregateMetrics(
  outcomes: SpinOutcome[],
  nSpins: number,
  configHash = "",
): MetricsResult {
  // First-pass accumulators — done in a single loop for cache locality.
  let sum = 0;
  let sumSq = 0;
  let hits = 0;
  let maxWin = 0;
  const featureCounts = new Map<string, number>();
  const histCounts = new Array<number>(
    HISTOGRAM_LOG_BOUNDARIES.length - 1,
  ).fill(0);
  // Spec 013 — per-feature award amounts, binned into the same log
  // buckets. Best-effort: populated only when the outcomes carry
  // `state_path` award steps (CPU producers); the wgpu/WASM path emits a
  // synthetic state_path without award detail, so this stays empty and
  // the field falls back to `{}` (R6 — the production GPU-aggregator path
  // supplies the canonical histogram for ≥1M-spin runs).
  const awardCounts = new Map<string, number[]>();

  for (const o of outcomes) {
    const w = o.winnings;
    sum += w;
    sumSq += w * w;
    if (w > 0) hits++;
    if (w > maxWin) maxWin = w;
    // 15-bucket log histogram. Bucket 0 = exact-zero; buckets 1–14
    // cover positive winnings.
    const bucketIdx = bucketIndexOf(w);
    histCounts[bucketIdx] = (histCounts[bucketIdx] ?? 0) + 1;
    for (const feat of o.features_fired) {
      featureCounts.set(feat, (featureCounts.get(feat) ?? 0) + 1);
    }
    foldAwardSteps(o, awardCounts);
  }

  const rtp = computeRtp(sum, sumSq, nSpins);
  const volatility = computeVolatility(sum, sumSq, nSpins);
  const histogram = buildHistogram(histCounts);
  const feature_triggers = buildFeatureTriggers(featureCounts, nSpins);
  const per_feature_award_histogram =
    buildPerFeatureAwardHistogram(awardCounts);

  return {
    rtp,
    volatility,
    hit_frequency: nSpins > 0 ? hits / nSpins : 0,
    histogram,
    max_win_observed: maxWin,
    max_win_bound: null,
    bonus_contribution: { total: 0, per_feature: {} },
    per_symbol_contribution: {},
    rtp_per_rule: {},
    hits_per_rule: {},
    feature_triggers,
    per_feature_award_histogram,
    observed_bounds: {},
    insufficient_sample_flags: [],
    schema_version: "1.5",
    n_spins: nSpins,
    config_hash: configHash,
  };
}

// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────

function bucketIndexOf(winnings: number): number {
  // Bucket 0 is exact-zero. Positive winnings: linear scan over the
  // 14 log-spaced upper bounds. 15-bucket array indexed 0..14.
  if (winnings === 0) return 0;
  for (let i = 1; i < HISTOGRAM_LOG_BOUNDARIES.length; i++) {
    const upper = HISTOGRAM_LOG_BOUNDARIES[i] ?? Number.POSITIVE_INFINITY;
    if (winnings <= upper) return i - 1;
  }
  return HISTOGRAM_LOG_BOUNDARIES.length - 2;
}

function computeRtp(sum: number, sumSq: number, n: number): RtpStat {
  if (n <= 0) {
    return { point: 0, ci_lo_95: 0, ci_hi_95: 0, ci_half_width: 0 };
  }
  const mean = sum / n;
  const variance = Math.max(0, sumSq / n - mean * mean);
  // 95% CI half-width via standard error of the mean. 1.96 ≈ z_{.975}.
  const sem = Math.sqrt(variance / n);
  const halfWidth = 1.96 * sem;
  return {
    point: mean,
    ci_lo_95: mean - halfWidth,
    ci_hi_95: mean + halfWidth,
    ci_half_width: halfWidth,
  };
}

function computeVolatility(
  sum: number,
  sumSq: number,
  n: number,
): VolatilityStat {
  if (n <= 0) {
    return { stdev: 0, class_: "low", stdev_noncoinciding: 0 };
  }
  const mean = sum / n;
  const variance = Math.max(0, sumSq / n - mean * mean);
  const stdev = Math.sqrt(variance);
  // Bucket boundaries match the spec-003.7 volatility-class breakpoints
  // (canonical in slot-engine-core/types.rs). Coarse but sufficient for
  // the headline panel until the Rust-side aggregator backfills.
  let class_: VolatilityStat["class_"];
  if (stdev < 3) class_ = "low";
  else if (stdev < 8) class_ = "medium";
  else if (stdev < 15) class_ = "high";
  else class_ = "very-high";
  return { stdev, class_, stdev_noncoinciding: stdev };
}

function buildHistogram(counts: readonly number[]): Histogram {
  const buckets: HistogramBucket[] = [];
  for (let i = 0; i < counts.length; i++) {
    const lo = HISTOGRAM_LOG_BOUNDARIES[i] ?? 0;
    const hi = HISTOGRAM_LOG_BOUNDARIES[i + 1] ?? lo;
    buckets.push({ lo, hi, count: counts[i] ?? 0 });
  }
  return { buckets, scale: "log" };
}

function buildFeatureTriggers(
  counts: Map<string, number>,
  nSpins: number,
): MetricsResult["feature_triggers"] {
  const result: MetricsResult["feature_triggers"] = {};
  for (const [name, count] of counts) {
    result[name] = {
      count,
      rate: nSpins > 0 ? count / nSpins : 0,
    };
  }
  return result;
}

/** Fixed-point scale for `detail.amount_fp` (matches
 * `slot-engine-core::fp::FIXED_POINT_SCALE`): award amount = fp / 10000. */
const FIXED_POINT_SCALE = 10_000;

/**
 * Spec 013 — fold one spin's `state_path` award steps into the per-feature
 * award-amount bucket accumulator. Mirrors the reference attribution
 * rule: an `award` step is credited to its executing `state` iff that
 * state is one of the spin's fired features (innermost-feature rule);
 * base-game awards (state not in `features_fired`) are excluded. The
 * amount is `detail.amount_fp / FIXED_POINT_SCALE`, binned into the same
 * 15-element log boundaries as the global histogram.
 *
 * Best-effort + defensive: each `state_path` item is `unknown`, so every
 * field access is narrowed; a step missing the expected shape is skipped.
 */
function foldAwardSteps(
  outcome: SpinOutcome,
  awardCounts: Map<string, number[]>,
): void {
  const fired = new Set(outcome.features_fired);
  for (const raw of outcome.state_path) {
    if (typeof raw !== "object" || raw === null) continue;
    const step = raw as {
      action_kind?: unknown;
      state?: unknown;
      detail?: unknown;
    };
    if (step.action_kind !== "award") continue;
    const feature = step.state;
    if (typeof feature !== "string" || !fired.has(feature)) continue;
    const detail = step.detail;
    if (typeof detail !== "object" || detail === null) continue;
    const amountFp = (detail as { amount_fp?: unknown }).amount_fp;
    if (typeof amountFp !== "number") continue;
    const amount = amountFp / FIXED_POINT_SCALE;
    const bucketIdx = bucketIndexOf(amount);
    let counts = awardCounts.get(feature);
    if (counts === undefined) {
      counts = new Array<number>(HISTOGRAM_LOG_BOUNDARIES.length - 1).fill(0);
      awardCounts.set(feature, counts);
    }
    counts[bucketIdx] = (counts[bucketIdx] ?? 0) + 1;
  }
}

/**
 * Materialise the per-feature award histogram map, omitting features
 * with zero events (never zero-filled — matches the canonical
 * `MetricsResult.per_feature_award_histogram`). Keys are sorted so the
 * feature-picker order is stable across runs.
 */
function buildPerFeatureAwardHistogram(
  awardCounts: Map<string, number[]>,
): Record<string, Histogram> {
  const result: Record<string, Histogram> = {};
  for (const feature of [...awardCounts.keys()].sort()) {
    const counts = awardCounts.get(feature);
    if (counts === undefined || !counts.some((c) => c > 0)) continue;
    result[feature] = buildHistogram(counts);
  }
  return result;
}
